import type { FilterState } from '@/components/filterable-search-bar/type'
import { arrayMove } from '@dnd-kit/sortable'
import { allowsMultipleConditions, conditionValues } from './groupedFilterFields'
import type { FilterGroup, FilterKey, GroupJoiner } from './types'

let idCounter = 0
/** Shared with URL restore, which re-keys a group carrying no id. */
export const buildGroupId = (): string => {
	idCounter += 1
	return `grp-${idCounter}-${Date.now().toString(36)}`
}

/** How an incoming condition folds into the conditions a group already holds. */
export type ConditionCommitMode =
	/** One chip per key: replace any condition on the same field. */
	| 'merge'
	/** Numeric key: keep what is there and add another comparison. */
	| 'append'
	/** Numeric key, same panel session: refine the comparison it added. */
	| 'replaceLast'

const lastIndexForField = (conditions: FilterState[], field: string): number => {
	for (let i = conditions.length - 1; i >= 0; i--) {
		if (conditions[i].field === field) {
			return i
		}
	}
	return -1
}

/** 'merge' replaces by field: the value picker preselects existing values on reopen, so incoming already
 *  carries the user's complete selection rather than an addition. */
const foldCondition = (conditions: FilterState[], incoming: FilterState, mode: ConditionCommitMode): FilterState[] => {
	if (mode === 'append') {
		return [...conditions, incoming]
	}
	const index =
		mode === 'replaceLast'
			? lastIndexForField(conditions, incoming.field)
			: conditions.findIndex(c => c.field === incoming.field)
	if (index === -1) {
		return [...conditions, incoming]
	}
	const next = [...conditions]
	next[index] = incoming
	return next
}

/**
 * Mode for a condition committed from an add-filter menu. On a numeric key the panel's first apply adds a
 * comparison and later ones refine it — without that split, its auto-apply would stack a chip per apply.
 */
export const commitModeForField = (
	field: FilterKey,
	options: { enableDataTypeOperators?: boolean; isNewExpression?: boolean },
): ConditionCommitMode => {
	if (!allowsMultipleConditions(field, options.enableDataTypeOperators)) {
		return 'merge'
	}
	return options.isNewExpression ? 'append' : 'replaceLast'
}

export const createGroup = (condition: FilterState, joiner: GroupJoiner = 'or'): FilterGroup => ({
	id: buildGroupId(),
	operator: 'and',
	joiner,
	conditions: [condition],
})

export const addConditionToGroup = (
	groups: FilterGroup[],
	groupId: string,
	condition: FilterState,
	mode: ConditionCommitMode = 'merge',
): FilterGroup[] =>
	groups.map(g => (g.id === groupId ? { ...g, conditions: foldCondition(g.conditions, condition, mode) } : g))

export const updateGroupCondition = (
	groups: FilterGroup[],
	groupId: string,
	conditionId: string,
	condition: FilterState,
): FilterGroup[] =>
	groups.map(g =>
		g.id === groupId ? { ...g, conditions: g.conditions.map(c => (c.id === conditionId ? condition : c)) } : g,
	)

/** Removes the condition and prunes the group if it becomes empty. */
export const removeGroupCondition = (groups: FilterGroup[], groupId: string, conditionId: string): FilterGroup[] =>
	groups
		.map(g => (g.id === groupId ? { ...g, conditions: g.conditions.filter(c => c.id !== conditionId) } : g))
		.filter(g => g.conditions.length > 0)

export const removeGroup = (groups: FilterGroup[], groupId: string): FilterGroup[] =>
	groups.filter(group => group.id !== groupId)

/** Drops one value from a condition; the condition goes with its last value, and the group with its last condition. */
export const removeConditionValue = (
	groups: FilterGroup[],
	groupId: string,
	conditionId: string,
	value: string,
): FilterGroup[] =>
	groups
		.map(group =>
			group.id !== groupId
				? group
				: {
						...group,
						conditions: group.conditions.flatMap(condition => {
							if (condition.id !== conditionId) {
								return [condition]
							}
							const values = conditionValues(condition)
							const index = values.indexOf(value)
							if (index === -1) {
								return [condition]
							}
							const nextValues = values.filter((_, i) => i !== index)
							if (nextValues.length === 0) {
								return []
							}
							return [
								{
									...condition,
									value: nextValues,
									valueLabels: condition.valueLabels?.filter((_, i) => i !== index),
								},
							]
						}),
					},
		)
		.filter(group => group.conditions.length > 0)

export const splitGroupAtCondition = (
	groups: FilterGroup[],
	groupId: string,
	conditionId: string,
	joiner: GroupJoiner,
): FilterGroup[] =>
	groups.flatMap(group => {
		if (group.id !== groupId) {
			return [group]
		}
		const splitIndex = group.conditions.findIndex(condition => condition.id === conditionId)
		if (splitIndex <= 0) {
			return [group]
		}
		return [
			{ ...group, conditions: group.conditions.slice(0, splitIndex) },
			{
				...group,
				id: buildGroupId(),
				joiner,
				conditions: group.conditions.slice(splitIndex),
			},
		]
	})

export const setGroupJoiner = (groups: FilterGroup[], groupId: string, joiner: GroupJoiner): FilterGroup[] =>
	groups.map(g => (g.id === groupId ? { ...g, joiner } : g))

export const reorderConditionValues = (
	groups: FilterGroup[],
	groupId: string,
	conditionId: string,
	from: number,
	to: number,
): FilterGroup[] => {
	const group = groups.find(g => g.id === groupId)
	const condition = group?.conditions.find(c => c.id === conditionId)
	const values = condition ? conditionValues(condition) : []
	if (!condition || from === to || from < 0 || to < 0 || from >= values.length || to >= values.length) {
		return groups
	}
	const labels =
		condition.valueLabels && condition.valueLabels.length === values.length ? condition.valueLabels : undefined
	return updateGroupCondition(groups, groupId, conditionId, {
		...condition,
		value: arrayMove(values, from, to),
		valueLabels: labels ? arrayMove(labels, from, to) : condition.valueLabels,
	})
}
